import { Fragment, Suspense, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, cn, Spinner } from '@c2fo/liquidity';
import NavigationBar from '@/enterprise/components/OfferBuilder/NavigationBar';
import PageBottomPortal from '@/enterprise/components/PageBottomPortal/PageBottomPortal';
import useCreateMonduQuote, { MonduQuote } from '@/enterprise/data/useCreateMonduQuote';
import useMonduHandoffSkip from '@/enterprise/data/useMonduHandoffSkip';
import { BusinessDetailsStep } from './BusinessDetailsStep';
import { WIZARD_CONTENT } from './content';
import { GetAQuoteModal } from './GetAQuoteModal';
import { ManualCustomersStep } from './ManualCustomersStep';
import { STEP_ORDER, resolveStep } from './quoteWizardRouting';
import {
  AddedCustomer,
  BusinessDetailsDraft,
  QuoteWizardStep,
  buildCreateQuoteBody,
  customerKey,
  initialWizardState,
} from './quoteWizardState';

type StepStatus = 'complete' | 'current' | 'upcoming';

/**
 * Where a failed quote request is reported. A supplier who skipped the consent modal never saw it, so
 * opening it just to carry an error would disclose consent they already gave; they get the failure in
 * the wizard footer instead, which is sticky and so cannot be scrolled out of view.
 */
type QuoteErrorSurface = 'footer' | 'modal';

const circleClassByStatus: Record<StepStatus, string> = {
  complete: 'border-2 border-primary-500 bg-white text-primary-500',
  current: 'bg-primary-500 text-white',
  upcoming: 'border-2 border-stroke bg-gray-100 text-text-secondary',
};

const Stepper = ({ currentStepIndex }: { currentStepIndex: number }) => (
  <ol className="flex w-full items-start justify-center">
    {WIZARD_CONTENT.steps.map((stepInfo, idx) => {
      const status: StepStatus =
        idx < currentStepIndex ? 'complete' : idx === currentStepIndex ? 'current' : 'upcoming';

      return (
        <Fragment key={stepInfo.index}>
          {idx > 0 && (
            <li
              aria-hidden="true"
              className={cn(
                'mx-1 mt-5 h-0.5 w-4 flex-none sm:mx-3 sm:mt-6 sm:min-w-8 sm:max-w-36 sm:flex-1',
                idx <= currentStepIndex ? 'bg-primary-500' : 'bg-stroke'
              )}
            />
          )}
          {/* Narrow screens: the steps flex to share the row and the labels wrap inside them. A fixed
              width here would let a nowrap label overflow the viewport. */}
          <li
            aria-current={status === 'current' ? 'step' : undefined}
            data-status={status}
            className="flex min-w-0 flex-1 flex-col items-center gap-2.5 sm:w-12 sm:flex-none"
          >
            <span
              className={cn(
                'flex size-10 items-center justify-center rounded-full text-lg sm:size-12 sm:text-xl',
                circleClassByStatus[status]
              )}
            >
              {stepInfo.index}
            </span>
            <span className="text-center text-sm text-text-primary sm:whitespace-nowrap sm:text-base">
              {stepInfo.label}
            </span>
          </li>
        </Fragment>
      );
    })}
  </ol>
);

export const GetAQuoteWizard = () => {
  const { step: stepSegment } = useParams();
  const navigate = useNavigate();
  const [businessDetails, setBusinessDetails] = useState<BusinessDetailsDraft | null>(
    initialWizardState.businessDetails
  );
  const [customers, setCustomers] = useState<AddedCustomer[]>(initialWizardState.customers);
  const [quote, setQuote] = useState<MonduQuote | null>(initialWizardState.quote);
  const [isBusinessDetailsValid, setIsBusinessDetailsValid] = useState(false);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [quoteErrorSurface, setQuoteErrorSurface] = useState<QuoteErrorSurface | null>(null);
  const { canSkipModal } = useMonduHandoffSkip();
  const { mutateAsync: createQuote, isPending: isCreatingQuote } = useCreateMonduQuote();
  // Filled by the step while it is mounted. See `BusinessDetailsStepProps.submitRef`.
  const submitBusinessDetails = useRef<(() => void) | null>(null);

  // The address is the only source of the current step; there is no copy of it in state to disagree.
  const { step, shouldRedirect } = resolveStep(stepSegment, {
    hasBusinessDetails: businessDetails !== null,
    hasQuote: quote !== null,
  });
  const currentStepIndex = STEP_ORDER.indexOf(step);

  const canGoBack = currentStepIndex > 0;

  /**
   * Forward actions push and backward actions pop, so history always mirrors the path the supplier
   * actually walked. `relative: 'path'` keeps these resolving against the address rather than the route
   * tree, which is what makes a sibling step the obvious destination.
   */
  const goToStep = (target: QuoteWizardStep) => navigate(`../${target}`, { relative: 'path' });
  const goBack = () => navigate(-1);

  const handleBack = () => {
    if (canGoBack) {
      goBack();
    }
  };

  const handleBusinessDetailsSubmit = (draft: BusinessDetailsDraft) => {
    setBusinessDetails(draft);
    void goToStep('add-customers');
  };

  // The step blocks a duplicate name before calling this, so appending cannot collide.
  const handleAddCustomer = (customer: AddedCustomer) => setCustomers((current) => [...current, customer]);

  const handleRemoveCustomer = (key: string) =>
    setCustomers((current) => current.filter((customer) => customerKey(customer.name) !== key));

  /** Creates the quote and advances on success, reporting a failure on `errorSurface`. */
  const requestQuote = async (draft: BusinessDetailsDraft, errorSurface: QuoteErrorSurface): Promise<void> => {
    // Cleared up front so a retry spins against an empty slate rather than the previous failure.
    setQuoteErrorSurface(null);

    try {
      const createdQuote = await createQuote(buildCreateQuoteBody(draft));

      // A quote without a status of `quoted`, or without a range to show, has nothing for Review to
      // render. SEA distinguishes `supplier_not_identified` from `provider_failed`; telling them
      // apart in the UI needs copy that does not exist yet, so both read as one failure here.
      if (createdQuote.status !== 'quoted' || !createdQuote.supplierAprRange) {
        setQuoteErrorSurface(errorSurface);
        return;
      }

      setQuote(createdQuote);
      setIsConsentModalOpen(false);
      void goToStep('review-quote');
    } catch {
      // The hook already reported it to DataDog; the wizard only needs to show the failure.
      setQuoteErrorSurface(errorSurface);
    }
  };

  /**
   * A supplier who already acknowledged the current Mondu consent and already has a Mondu supplier
   * has nothing left to disclose, so Generate Quote posts directly. Everyone else — including every
   * supplier whose consent state is still loading or failed to load — gets the modal. Same rule as
   * `ExtendedNetworkFinance`.
   */
  const handleGenerateQuote = async (): Promise<void> => {
    if (!businessDetails) {
      return;
    }

    if (!canSkipModal) {
      setIsConsentModalOpen(true);
      return;
    }

    await requestQuote(businessDetails, 'footer');
  };

  const closeConsentModal = (): void => {
    setIsConsentModalOpen(false);
    setQuoteErrorSurface(null);
  };

  /**
   * Review Quote has no footer. Its actions live on the page, and this one returns to Add Customers —
   * so it pops like every other backward move. Pushing here would leave the footer Back on Add
   * Customers popping to Review Quote instead of Business Details.
   */
  const handleModifyCustomerSelection = () => goBack();

  // Checked after the hooks so the step bodies below never render against a draft they need and lack.
  // Replacing rather than pushing: a push would put the unreachable address back in history, and the
  // next back press would re-trigger this redirect and pin the supplier here.
  if (shouldRedirect) {
    return <Navigate to={`../${step}`} relative="path" replace />;
  }

  return (
    <div className="mx-auto max-w-full px-4 py-6 md:max-w-7xl">
      <div className="flex flex-col items-center gap-8">
        <div className="flex flex-col items-center space-y-2 text-center text-text-primary">
          <div className="text-4xl font-bold">{WIZARD_CONTENT.title}</div>
          <div className="text-base">
            <span className="font-bold">{WIZARD_CONTENT.subtitleEmphasis}</span>
            {WIZARD_CONTENT.subtitle}
          </div>
        </div>

        <Stepper currentStepIndex={currentStepIndex} />
      </div>

      <div className="mt-8">
        {step === 'business-details' && (
          // The step prefills its contact fields from the logged-in user, so it suspends.
          <Suspense
            fallback={
              <div className="flex h-64 w-full items-center justify-center">
                <Spinner className="h-12 w-12 fill-primary-500" />
              </div>
            }
          >
            <BusinessDetailsStep
              draft={businessDetails}
              onSubmit={handleBusinessDetailsSubmit}
              onValidityChange={setIsBusinessDetailsValid}
              submitRef={submitBusinessDetails}
            />
          </Suspense>
        )}
        {step === 'add-customers' && (
          <ManualCustomersStep customers={customers} onAdd={handleAddCustomer} onRemove={handleRemoveCustomer} />
        )}
        {step === 'review-quote' && (
          // `data-quote-id` stands in until Review Quote renders the stored quote for real.
          <div
            data-testid="review-placeholder"
            data-quote-id={quote?.quoteId}
            className="flex flex-col items-start gap-4"
          >
            <div>Review Quote placeholder (slice 5)</div>
            <Button variant="tertiary" onClick={handleModifyCustomerSelection}>
              {WIZARD_CONTENT.actions.modifyCustomerSelection}
            </Button>
          </div>
        )}
      </div>

      {step !== 'review-quote' && (
        <PageBottomPortal>
          <NavigationBar>
            {/* Lets a test scope the actions and the failure to the footer rather than the step body. */}
            <div
              data-testid="wizard-footer"
              className="flex w-full shrink-0 flex-col items-center gap-4 md:ml-auto md:w-fit md:flex-row"
            >
              {/* In the footer rather than the step body: the footer is sticky, so the supplier can press
                  Generate Quote from any scroll position and an error in the step content would be
                  off-screen. Sized to its text and placed before Back so it reads as part of this row;
                  description only, since a title would make it taller than the buttons beside it. */}
              {step === 'add-customers' && quoteErrorSurface === 'footer' && (
                <Alert type="error" description={WIZARD_CONTENT.requestError.description} />
              )}
              <Button className="w-full md:w-fit" variant="secondary" onClick={handleBack} disabled={!canGoBack}>
                {WIZARD_CONTENT.actions.back}
              </Button>
              {step === 'business-details' ? (
                // The footer is portalled away from the step body, so Next calls the step's submit
                // handler directly rather than relying on a `form` association.
                <Button
                  className="w-full md:w-fit"
                  onClick={() => submitBusinessDetails.current?.()}
                  disabled={!isBusinessDetailsValid}
                >
                  {WIZARD_CONTENT.actions.next}
                </Button>
              ) : (
                // Disabled without a draft: every required body field comes from step 1, which only
                // lets Next through once it is valid.
                <Button
                  className="w-full md:w-fit"
                  variant="cta"
                  onClick={() => void handleGenerateQuote()}
                  loading={isCreatingQuote}
                  disabled={!businessDetails || isCreatingQuote}
                >
                  {WIZARD_CONTENT.actions.generateQuote}
                </Button>
              )}
            </div>
          </NavigationBar>
        </PageBottomPortal>
      )}

      <GetAQuoteModal
        open={isConsentModalOpen}
        onClose={closeConsentModal}
        onAcknowledge={() => {
          if (businessDetails) {
            void requestQuote(businessDetails, 'modal');
          }
        }}
        isSubmitting={isCreatingQuote}
        showRequestError={quoteErrorSurface === 'modal'}
      />
    </div>
  );
};
